import React, { useState, useEffect, useRef } from 'react';
import './SelectBox.css';

interface Option {
    name: string;
    key: string;
}

interface SelectBoxProps {
    options: Option[];
    onSelect: (name: string, option: Option | Option[], isMulti: Boolean) => void;
    name: string;
    defaultSelected?: Option | Option[];
    isMulti?: boolean;
}

const SelectBox: React.FC<SelectBoxProps> = ({ options, onSelect, name, defaultSelected, isMulti = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<Option[]>(Array.isArray(defaultSelected) ? defaultSelected : defaultSelected ? [defaultSelected] : []);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        setSelectedOptions(Array.isArray(defaultSelected) ? defaultSelected : defaultSelected ? [defaultSelected] : []);
    }, [defaultSelected]);

    const toggleDropdown = () => setIsOpen(!isOpen);

    const handleOptionClick = (option: Option) => {
        let newSelectedOptions: Option[];

        if (isMulti) {
            if (option.key === '') { // "All" option logic
                if (selectedOptions.some((selectedOption) => selectedOption.key === '')) {
                    newSelectedOptions = [];
                } else {
                    newSelectedOptions = options;
                }
            } else {
                if (selectedOptions.some((selectedOption) => selectedOption.key === option.key)) {
                    newSelectedOptions = selectedOptions.filter((selectedOption) => selectedOption.key !== option.key);
                } else {
                    newSelectedOptions = [...selectedOptions, option];
                }

                // Select or deselect "All" option based on the selection
                if (newSelectedOptions.length === options.length - 1 && !newSelectedOptions.some((opt) => opt.key === '')) {
                    newSelectedOptions.push({ name: 'All', key: '' });
                } else if (newSelectedOptions.length < options.length && newSelectedOptions.some((opt) => opt.key === '')) {
                    newSelectedOptions = newSelectedOptions.filter((opt) => opt.key !== '');
                }
            }
        } else {
            newSelectedOptions = [option];
            setIsOpen(false);
        }

        setSelectedOptions(newSelectedOptions);
        onSelect(name, isMulti ? newSelectedOptions : option, isMulti);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const displaySelectedOptions = () => {
        if (isMulti) {
            if (selectedOptions.some((opt) => opt.key === '')) {
                return 'All';
            }
            if (selectedOptions.length > 3) {
                return `${selectedOptions.length} selected`;
            }
            return selectedOptions.length > 0 ? selectedOptions.map(option => option.name).join(', ') : 'Select options';
        }
        return selectedOptions[0]?.name || 'Select an option';
    };

    const filteredOptions = options.filter(option =>
        option.name.toLowerCase().includes(searchText.toLowerCase())
    );

    return (
        <div className="custom-select-container" ref={dropdownRef}>
            <div className="custom-select-box" onClick={toggleDropdown}>
                {displaySelectedOptions()}
            </div>
            {isOpen && (
                <div className={`custom-select-dropdown ${isMulti ? 'multiselect-dropdown' : ''}`}>
                    {isMulti && (
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="custom-select-search"
                        />
                    )}
                    {filteredOptions.map((option) => (
                        <div
                            key={option.key}
                            className="custom-select-option"
                            onClick={() => handleOptionClick(option)}
                        >
                            {isMulti && (
                                <input
                                    type="checkbox"
                                    checked={selectedOptions.some((selectedOption) => selectedOption.key === option.key)}
                                    onChange={() => handleOptionClick(option)}
                                    className='m-1'
                                />
                            )}
                            {option.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SelectBox;
